import { MainPageContainer, UpperInfoContainer } from 'components/PageLayout';
import { convertToDate, useCoreMIReport } from 'pages/hooks';
import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { dateToString, formatDate, formatDateTime } from 'utils/helpers';
import {
  CORE_MI_REPORT,
  columns,
  CORE_MI_REPORT_TITLE_INFO_MESSAGE,
  CORE_MI_REPORT_GRID_TITLE,
} from './constants';
import { AxiosError } from 'axios';
import { createReportURL } from 'core/routes';
import { errorMessages } from 'api/resources/responseMessages/errorMessages';
import { NoReportDataMessage } from 'components/ReportsComponents/NoReportDataMessage';
import { ReportGridContainer } from 'components/ReportGridContainer';
import { ReportBackButton } from 'components/ReportsComponents/ReportBackButton';
import { ReportHeader } from 'components/ReportsComponents/ReportHeader';
import { ReportFormCoreMI } from 'components/ReportForms/ReportFormCoreMI';
import { customizeCell, valuesConverter } from './helpers';
import { reportLogger } from 'pages/ReportsDetails/reportLogger';
import { ReportProps } from 'pages/ReportsDetails/hooks';
import ReportGridNew from 'components/ReportGrid_New';
import { noPDFexportFormats } from 'components/ReportGrid_New/constants';
import { Row } from '@devexpress/dx-react-grid';

export function CoreMIReport({
  reportPath,
  setErrorMessage,
  setToast,
}: Readonly<ReportProps>) {
  const navigate = useNavigate();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const urlParamsValue = {
    endDate: urlParams.get('endDate'),
    startDate: urlParams.get('startDate'),
    isInitialWorkCompletedDate:
      urlParams.get('isInitialWorkCompletedDate') == 'true',
  };

  const queryEnabled = !!urlParamsValue.startDate && !!urlParamsValue.endDate;

  const { isReportFetching, refetchReport, reportData } = useCoreMIReport({
    startDate: urlParamsValue.startDate,
    endDate: urlParamsValue.endDate,
    isInitialWorkCompletedDate: urlParamsValue.isInitialWorkCompletedDate,
    enabled: queryEnabled,
    onError: handleError,
  });

  const rows = useMemo<Row[] | undefined>(() => reportData ? valuesConverter(reportData) : undefined, [reportData]);

  const [startDate, setStartDate] = useState<Date | null>(
    convertToDate(urlParamsValue.startDate) ?? null
  );
  const [endDate, setEndDate] = useState<Date | null>(
    convertToDate(urlParamsValue.endDate) ?? null
  );
  const [isInitialWorkCompletedDate, setIsInitialWorkCompletedDate] =
    useState<boolean>(urlParamsValue.isInitialWorkCompletedDate);

  useEffect(() => {
    navigateIfPossible();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    urlParamsValue.startDate,
    urlParamsValue.endDate,
    urlParamsValue.isInitialWorkCompletedDate,
  ]);

  const reportHeaderTagLine = useMemo(
    () =>
      `Report produced on ${formatDateTime(new Date())} from ${formatDate(startDate)} to ${formatDate(endDate)}`,
    [startDate, endDate]
  );

  return (
    <MainPageContainer isLoading={isReportFetching} direction="column" wrap="nowrap">
      <UpperInfoContainer direction="column">
        <ReportBackButton />
        <ReportHeader
          reportName={CORE_MI_REPORT}
          infoMessage={CORE_MI_REPORT_TITLE_INFO_MESSAGE}
        />
        <ReportFormCoreMI
          isBusy={isReportFetching}
          endDate={endDate}
          startDate={startDate}
          setEndDate={setEndDate}
          setStartDate={setStartDate}
          handleSubmit={handleSubmit}
          isInitialWorkCompletedDate={isInitialWorkCompletedDate}
          setIsInitialWorkCompletedDate={setIsInitialWorkCompletedDate}
        />
      </UpperInfoContainer>

      {!isReportFetching && rows?.length === 0 && <NoReportDataMessage />}

      <ReportGridContainer direction="column">
        <ReportGridNew
          columns={columns}
          customizeCell={customizeCell}
          displayGroupingPanel
          exportFormats={noPDFexportFormats}
          headerTitleForSheet={CORE_MI_REPORT}
          isLoading={isReportFetching}
          reportHeaderTagLine={reportHeaderTagLine}
          rows={rows}
          title={CORE_MI_REPORT_GRID_TITLE}
        />
      </ReportGridContainer>
    </MainPageContainer>
  );

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (
      urlParamsValue.endDate == dateToString(endDate) &&
      urlParamsValue.startDate == dateToString(startDate) &&
      urlParamsValue.isInitialWorkCompletedDate == isInitialWorkCompletedDate
    )
      refetchReport();
    else navigateIfPossible();
  }

  function navigateIfPossible() {
    if (!!startDate && !!endDate) {
      navigate(
        createReportURL(
          {
            startDate: dateToString(startDate),
            endDate: dateToString(endDate),
            isInitialWorkCompletedDate: isInitialWorkCompletedDate.toString(),
          },
          reportPath,
        ), { replace: true });
    }
  }

  function handleError(error: AxiosError) {
    reportLogger(error);
    setErrorMessage(errorMessages.reports(CORE_MI_REPORT));
    setToast(true);
  }
}
